import React, { useEffect, useState, useCallback } from 'react';
import { HardDrive, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getOfflineSnapshotInfo, clearOfflineSnapshot } from '../utils/offlineSnapshot';
import { formatRelativeTime } from '../utils/relativeTime';
import { Button } from './ui/button';

// Human-friendly byte formatter: 512 B / 12.3 KB / 1.4 MB.
function formatBytes(bytes) {
  if (!bytes || bytes < 1) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// Locale-aware "N min ago" using Intl.RelativeTimeFormat so non-English
// users don't get a half-translated line. Falls back to the shared
// (English) formatter if the API is unavailable.
function formatRelativeLocalized(ts, lang) {
  if (!ts) return '\u2014';
  try {
    const rtf = new Intl.RelativeTimeFormat(lang || 'en', { numeric: 'auto' });
    const sec = Math.round((Number(ts) - Date.now()) / 1000);
    const abs = Math.abs(sec);
    if (abs < 60) return rtf.format(sec, 'second');
    const min = Math.round(sec / 60);
    if (Math.abs(min) < 60) return rtf.format(min, 'minute');
    const hr = Math.round(min / 60);
    if (Math.abs(hr) < 24) return rtf.format(hr, 'hour');
    return rtf.format(Math.round(hr / 24), 'day');
  } catch {
    return formatRelativeTime(ts);
  }
}

/**
 * Offline Cache Manager — a compact Settings section (Drive mode only)
 * that reports the size of the browse-only offline mirror and offers a
 * one-tap "Clear offline cache" button.
 *
 * Renders nothing when no snapshot exists yet, so the section stays
 * invisible until there's something to manage.
 */
export const OfflineCacheManager = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const userKey = user?.email || user?.id || 'anon';

  const [info, setInfo] = useState(null); // { count, bytes, saved_at } | null
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const next = await getOfflineSnapshotInfo(userKey);
    setInfo(next);
    setLoading(false);
  }, [userKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleClear = async () => {
    setClearing(true);
    const ok = await clearOfflineSnapshot(userKey);
    setClearing(false);
    if (ok) {
      setInfo(null);
      toast.success(t('offlineCacheCleared') || 'Offline cache cleared');
    } else {
      toast.error(t('offlineCacheClearFailed') || 'Could not clear the offline cache');
    }
  };

  // Nothing cached yet (or still loading with no prior info) → hide.
  if (loading || !info) return null;

  return (
    <div
      className="space-y-3 border-t pt-4"
      data-testid="offline-cache-section"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div
            className="flex items-center gap-1.5 text-base font-medium"
            style={{ color: 'hsl(143, 28%, 25%)' }}
          >
            <HardDrive className="w-4 h-4" aria-hidden="true" />
            {t('offlineCacheTitle') || 'Offline cache'}
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'hsl(125, 12%, 40%)' }}>
            {t('offlineCacheBlurb') ||
              'A read-only copy of your library kept on this device so you can still browse when offline.'}
          </p>
          <p
            className="text-sm mt-1.5"
            style={{ color: 'hsl(143, 28%, 32%)' }}
            data-testid="offline-cache-stats"
          >
            {(t('offlineCacheStats') || '{count} {plantWord} \u00b7 {size} \u00b7 saved {when}')
              .replace('{count}', String(info.count))
              .replace('{plantWord}', info.count === 1 ? (t('plant') || 'plant') : (t('plants') || 'plants'))
              .replace('{size}', formatBytes(info.bytes))
              .replace('{when}', formatRelativeLocalized(info.saved_at, language))}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={clearing}
          className="flex-shrink-0 gap-1.5"
          aria-label={t('offlineCacheClear') || 'Clear offline cache'}
          data-testid="offline-cache-clear-button"
        >
          {clearing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
          )}
          {t('offlineCacheClear') || 'Clear offline cache'}
        </Button>
      </div>
    </div>
  );
};

export default OfflineCacheManager;
